/**
 * Agent configurations and constants
 * 
 * @module Agents
 */
import OpenAILogo from '@/agents/landing-page-comparator/assets/open-ai-logo.svg'
import FireCrawlLogo from '@/agents/landing-page-comparator/assets/firecrawl-logo.jpeg'
import LandingPageAvatar from "@/agents/landing-page-comparator/assets/avatar.png";
import LinkedinPostAvatar  from '@/agents/influencer-styled-linkedin-post-generator/assets/avatar.png';
import ApifyLogo from "@/agents/influencer-styled-linkedin-post-generator/assets/apify-logo.png";
import LeadEnrichmentAvatar from "@/agents/lead-enrichment/assets/avatar.jpg";
import ApolloLogo from "@/agents/lead-enrichment/assets/apollo.png";
import GeminiLogo from "@/agents/lead-enrichment/assets/gemini.png";
import HubSpotLogo from "@/agents/lead-enrichment/assets/hubspot-logo.webp";
import GoogleAds from "@/agents/google-ads-campaign-analyser/assets/google-ads.webp";
import LinkedIn from "@/agents/linkedin-ads-campaign-analyser/assets/linkedin.webp";
import SlackLogo from "@/agents/google-ads-campaign-analyser/assets/slack.png";
import TavilyLogo from "@/agents/lead-enrichment/assets/tavily.jpeg";
import ProspectCallOpportunityAnalyzerAvatar from "@/agents/prospect-call-opportunity-analyzer/assets/avatar.png";
import GoogleAdsCampaignAnalyserAvatar from "@/agents/google-ads-campaign-analyser/assets/avatar.png";
import BlogPostContentStrategizerAvatar from "@/agents/blog-post-content-strategizer/assets/avatar.png";
import LinkedInAdsCampaignAnalyserAvatar from "@/agents/linkedin-ads-campaign-analyser/assets/avatar.png";
import BlogPostRefreshAgentAvatar from "@/agents/blog-post-content-refresher/assets/avatar.png";
import NegativeKeywordAnalyzerAvatar from "@/agents/negative-keyword-analyzer/assets/avatar.png";
import SalesBattleCardGeneratorAvatar from "@/agents/sales-battle-card-generator/assets/avatar.png";
import SeoAuditAvatar from "@/agents/seo-audit/assets/avatar.png";

export interface AgentTool {
  id: string;
  name: string;
  logo: string;
  connected: boolean;
}

export interface AgentConfig {
  id: number;
  name: string;
  displayName: string;
  description: string;
  avatar: string;
  tools: AgentTool[];
  ctaText?: string; // Configurable text for the "New Analysis" button
  currentTask?: {
    title: string;
    subtitle?: string;
  };
  configurationSource?: {
    type: 'agent_instance' | 'tenant_settings';
    key?: string; // For tenant_settings, specify which settings key to check
    requiredFields?: string[]; // Which fields must exist to be considered configured
  };
  // Optional chat configuration to enable/disable agent chat UI and defaults
  chat?: {
    enabled: boolean;
    title?: string;
    initialMessage?: string; // default assistant message when chat opens
  };
  // Optional UI configuration
  ui?: {
    showSecondarySidebar?: boolean; // Whether to show the secondary sidebar (default: true)
    showSidebar?: boolean; // Whether to show the left sidebar (default: true)
    showChatPanel?: boolean; // Whether to show the chat panel (default: false)
    allowContentOverflow?: boolean; // Whether to allow content area to overflow and scroll (default: false, which uses overflow-hidden)
    showNewAnalysisButton?: boolean; // Whether to show the "+ New Analysis" button in the header (default: true)
  };
}

// Short name for the global chat agent
export const CHAT_AGENT_SHORT_NAME = 'global-chat-agent';

// Short name for the report-builder chat agent (powers the Report Studio chat pane)
export const REPORT_BUILDER_AGENT_SHORT_NAME = 'report-builder';

// Agent configurations
export const AGENT_CONFIGS: Record<string, AgentConfig> = {
  "landing-page-comparator": {
    id: 1,
    name: "landing-page-comparator",
    displayName: "Landing Page Comparator",
    description: "Researches your competitors' blog posts for a set of keywords to recommend a post outline and strategy to outrank their articles",
    avatar: LandingPageAvatar,
    ctaText: "New Comparison",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
      { id: "firecrawl", name: "Firecrawl", logo: FireCrawlLogo, connected: true },
    ],
  },
  "influencer-styled-linkedin-post-generator": {
    id: 2,
    name: "Influencer Styled LinkedIn Post Generator",
    displayName: "Influencer Styled LinkedIn Post Generator",
    description: "Generates a draft LinkedIn post for a given topic based on the style of your favorite LinkedIn influencer.",
    avatar: LinkedinPostAvatar,
    ctaText: "Generate New Post",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
      { id: "apify", name: "Apify", logo: ApifyLogo, connected: true },
    ],
  },
  "lead-enrichment": {
    id: 3,
    name: "lead-enrichment",
    displayName: "Lead Enrichment",
    description: "When a contact gets created in HubSpot it enriches it with the country information.",
    avatar: LeadEnrichmentAvatar,
    ctaText: "Enrich New Leads",
    tools: [
      { id: "hubspot", name: "HubSpot", logo: HubSpotLogo, connected: true },
      { id: "apollo", name: "Apollo.io", logo: ApolloLogo, connected: true },
      { id: "gemini", name: "Gemini", logo: GeminiLogo, connected: true },
      { id: "tavily", name: "Tavily", logo: TavilyLogo, connected: true },
    ],
    configurationSource: {
      type: 'agent_instance',
      requiredFields: ['qualification_score_rules']
    },
  },
  "prospect-call-opportunity-analyzer": {
    id: 4,
    name: "prospect-call-opportunity-analyzer",
    displayName: "Prospect Call Opportunity Analyzer",
    description: "Analyzes sales calls to identify opportunities and automatically posts the summary in Confluence.",
    avatar: ProspectCallOpportunityAnalyzerAvatar,
    ctaText: "Analyze a new meeting",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
    ],
  },
  "google-ads-campaign-analyser": {
    id: 5,
    name: "Google Ads Campaign Analyser",
    displayName: "Google Ads Campaign Analyser",
    description: "Analyzes your Google Ads campaigns comprehensively to detect anomalies, flag campaigns for review, and suggest actions.",
    avatar: GoogleAdsCampaignAnalyserAvatar,
    ctaText: "New Analysis",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
      { id: "google_ads", name: "Google Ads", logo: GoogleAds, connected: true },
      { id: "slack", name: "Slack", logo: SlackLogo, connected: true },
    ],
    configurationSource: {
      type: 'agent_instance',
      key: 'google_ads',
      requiredFields: ['credential_id', 'account_id']
    },
    chat: {
      enabled: true,
    },
    ui: {
      showChatPanel: true
    }
  },
  "blog-post-content-strategizer": {
    id: 6,
    name: "blog-post-content-strategizer",
    displayName: "Blog Post Content Brief Generator",
    description: "Analyze post content and provides strategy",
    ctaText: "Generate New Brief",
    avatar: BlogPostContentStrategizerAvatar,
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
    ],
  },
  "blog-post-publisher": {
    id: 7,
    name: "blog-post-publisher",
    displayName: "Blog post publisher",
    description: "Publishes blog posts",
    avatar: LinkedinPostAvatar,
    ctaText: "Publish New Post",
    tools: [
      { id: "gemini", name: "Gemini", logo: GeminiLogo, connected: true },
    ],
  },
  "linkedin-ads-campaign-analyser": {
    id: 8,
    name: "LinkedIn Ads Campaign Analyser",
    displayName: "LinkedIn Ads Campaign Analyser",
    description: "Analyzes your LinkedIn Ads campaigns comprehensively to detect anomalies, flag campaigns for review, and suggest actions.",
    avatar: LinkedInAdsCampaignAnalyserAvatar,
    ctaText: "New Analysis",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
      { id: "linkedin", name: "LinkedIn Ads", logo: LinkedIn, connected: true },
      { id: "slack", name: "Slack", logo: SlackLogo, connected: true },
    ],
    configurationSource: {
      type: 'agent_instance',
      key: 'linkedin',
      requiredFields: ['credential_id', 'account_id']
    },
  },
  "blog-post-content-refresher": {
    id: 9,
    name: "blog-post-content-refresher",
    displayName: "Blog Post Refresh",
    description: "Upload a CSV file with blog post URLs to refresh and optimize your content for better performance.",
    avatar: BlogPostRefreshAgentAvatar,
    ctaText: "New Refresh",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
    ],
    ui: {
      showSidebar: false
    },
  },
 "negative-keyword-analyzer": {
  id: 10,
  name: "negative-keyword-analyzer",
  displayName: "Negative Keyword Analyzer",
  description: "Identifies negative keywords for Google Ads campaigns to improve ad targeting and reduce wasted spend.",
  avatar: NegativeKeywordAnalyzerAvatar,
  ctaText: "New Analysis",
  tools: [
    { id: "google_ads", name: "Google Ads", logo: GoogleAds, connected: true },
  ],
  configurationSource: {
    type: 'agent_instance',
    key: 'google_ads',
    requiredFields: ['credential_id', 'account_id']
  },
},
 "sales-battle-card-generator": {
  id: 11,
  name: "sales-battle-card-generator",
  displayName: "Sales Battle Card Generator",
  description: "Generates competitive battle cards by analyzing your product vs competitor products",
  avatar: SalesBattleCardGeneratorAvatar,
  tools: [
    { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
  ],
  chat: {
    enabled: true,
  },
  ui: {
    showChatPanel: true,
    showSidebar: false,
    showNewAnalysisButton: false
  }
},
  "answer-engine-optimization": {
    id: 30,
    name: "answer-engine-optimization",
    displayName: "Answer Engine Optimization",
    description: "Track and optimize brand visibility across AI answer engines like ChatGPT, Perplexity, and Gemini",
    avatar: OpenAILogo,
    ctaText: "Run Analysis",
    tools: [
      { id: "open_ai", name: "OpenAI", logo: OpenAILogo, connected: true },
      { id: "perplexity", name: "Perplexity", logo: OpenAILogo, connected: true },
      { id: "gemini", name: "Gemini", logo: GeminiLogo, connected: true },
    ],
    configurationSource: {
      type: 'agent_instance',
      requiredFields: ['prompts']
    },
    ui: {
      showNewAnalysisButton: false
    }
  },
  "seo-audit": {
    id: 31,
    name: "seo-audit",
    displayName: "SEO Audit",
    description: "Runs a one-shot SEO audit using Ahrefs — domain metrics, traffic history, country split, competitor comparison, top pages, ranking keywords, and content gap.",
    avatar: SeoAuditAvatar,
    ctaText: "Run Audit",
    tools: [],
    ui: {
      showChatPanel: false,
      showSidebar: false,
      showNewAnalysisButton: false
    }
  },
};

// Helper function to get agent config by ID
export const getAgentConfig = (name: string): AgentConfig | null => {
  return AGENT_CONFIGS[name] || null;
};

// Helper function to get all agent IDs
export const getAllAgentIds = (): string[] => {
  return Object.keys(AGENT_CONFIGS);
};

// Helper function to get connected tools for an agent
export const getConnectedTools = (agentId: string): AgentTool[] => {
  const config = getAgentConfig(agentId);
  return config ? config.tools.filter(tool => tool.connected) : [];
};

// Helper function to generate agent URL
export const getAgentUrl = (agentName: string): string => {
  return `/ui/agent/${agentName}`;
};

/**
 * Demo specific agent constants and functions
 */

// avatar assets
import Avatar1 from '@/demo/assets/avatars/avatar5.jpg'
import Avatar2 from '@/demo/assets/avatars/avatar6.jpg'
import Avatar4 from '@/demo/assets/avatars/avatar17.png'
import Avatar5 from '@/demo/assets/avatars/avatar9.png'
import Avatar6 from '@/demo/assets/avatars/avatar12.png'
import Avatar15 from '@/demo/assets/avatars/avatar15.png'
import Avatar18 from '@/demo/assets/avatars/avatar18.png'
import BlogPostRefreshAvatar from '@/demo/assets/avatars/avatar25.png'
import LinkedInCampaignAnalyserAvatar from '@/demo/assets/avatars/avatar20.png'
import ContentRepurposeAvatar from '@/demo/assets/avatars/avatar26.png';


export type AgentCategory = "Marketing" | "Sales" | "Finance" | "Engineering" | "Other";

export interface AgentCategoryMapping {
  agent_id: string;
  category: AgentCategory;
  avatar?: string; // Avatar URL or imported asset
  demoOnly?: boolean; // If true, only show to demo-enabled users
}

export type DemoAgentCategoryMap = AgentCategoryMapping[];

// List of authorized users who should see demo specific agents
export const DEMO_ENABLED_USERS: string[] = [
  'mari@strivelabs.ai',
  'satwik@strivelabs.ai',
  'gb@strivelabs.ai',
  'raghuram@strivelabs.ai',
  'arunkumar@strivelabs.ai',
  'jahirbasha.nabadulla@strivelabs.ai',
  'sakthivel@strivelabs.ai',
  'satwikhebbar@gmail.com',
  'gayathri@asdr.in',
  'kamalika.azhakar@strivelabs.ai',
  'kishor@strivelabs.ai'
];

const DEV_AGENT_CATEGORY_MAP: DemoAgentCategoryMap = [
  // agents which are self served
  { agent_id: "1", category: "Marketing", avatar: Avatar1 }, // Landing page comparator
  { agent_id: "29", category: "Marketing", avatar: GoogleAdsCampaignAnalyserAvatar }, // Google Ads Campaign Analyser
  { agent_id: "3", category: "Marketing", avatar: Avatar2 }, // Influencer styled LinkedIn Post Generator
  { agent_id: "4", category: "Marketing", avatar: BlogPostContentStrategizerAvatar}, // Blog Post Content Strategizer
  { agent_id: "31", category: "Marketing", avatar: SeoAuditAvatar }, // SEO Audit
  // demo only agents
  { agent_id: "2", category: "Sales", avatar: Avatar4, demoOnly: true }, // lead enrichment
  { agent_id: "5", category: "Marketing", avatar: Avatar6, demoOnly: true }, // Keyword Optimizer
  { agent_id: "6", category: "Marketing", avatar: Avatar1, demoOnly: true }, // Lead Funnel Analyser
  { agent_id: "8", category: "Sales", avatar: ProspectCallOpportunityAnalyzerAvatar, demoOnly: true }, // Prospect Call Opportunity Analyzer
  { agent_id: "9", category: "Sales", avatar: Avatar1, demoOnly: true }, // Outbound Call AI Agent
  { agent_id: "10", category: "Finance", avatar: Avatar5, demoOnly: true }, // Recki
  { agent_id: "11", category: "Sales", avatar: Avatar6, demoOnly: true }, // Sales BANT Qualifier
  { agent_id: "12", category: "Marketing", avatar: BlogPostRefreshAvatar, demoOnly: true }, // Blog Post Refresh
  { agent_id: "13", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar, demoOnly: true }, // LinkedIn campaign analyzer demo agent
  { agent_id: "14", category: "Marketing", avatar: ContentRepurposeAvatar, demoOnly: true }, // Content repurposing agent
  // Add more mappings as needed
];

// Add staging-specific mappings here
const STAGING_AGENT_CATEGORY_MAP: DemoAgentCategoryMap = [
  // agents which are self served
  { agent_id: "2", category: "Marketing", avatar: Avatar15 }, // Landing Page Comparator
  { agent_id: "3", category: "Marketing", avatar: Avatar18 }, // Influencer Styled LinkedIn Post Generator
  { agent_id: "12", category: "Marketing", avatar: GoogleAdsCampaignAnalyserAvatar }, // Google ads agent
  { agent_id: "15", category: "Marketing", avatar: BlogPostContentStrategizerAvatar }, // Blog Post Content Strategizer
  { agent_id: "14", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar }, // LinkedIn campaign analyzer
  { agent_id: "18", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar }, // LinkedIn campaign analyzer
  { agent_id: "26", category: "Marketing", avatar: Avatar6 }, // Blog Post Refresh - RZP
  { agent_id: "27", category: "Marketing", avatar: NegativeKeywordAnalyzerAvatar}, // Negative Keyword Analyzer
  { agent_id: "29", category: "Marketing", avatar: Avatar1}, // AEO
  { agent_id: "39", category: "Marketing", avatar: SeoAuditAvatar }, // SEO Audit

  // demo only agents
  { agent_id: "8", category: "Sales", avatar: Avatar5, demoOnly: true }, // BANT Qualifier Agent
  { agent_id: "9", category: "Sales", avatar: Avatar5, demoOnly: true }, // Outbound Call AI Agent
  { agent_id: "10", category: "Marketing", avatar: Avatar5, demoOnly: true }, // Keyword Optimizer
  { agent_id: "1", category: "Marketing", avatar: Avatar5, demoOnly: true }, // Lead Funnel Analyser
  { agent_id: "6", category: "Marketing", avatar: Avatar5, demoOnly: true }, // Google Ads Campaign Analyser Demo
  { agent_id: "4", category: "Sales", avatar: Avatar5, demoOnly: true }, // Lead Enrichment
  { agent_id: "13", category: "Finance", avatar: Avatar5, demoOnly: true }, // recki
  { agent_id: "17", category: "Marketing", avatar: BlogPostRefreshAvatar, demoOnly: true }, // Blog Post Refresh - Hevo
  { agent_id: "23", category: "Marketing", avatar: ContentRepurposeAvatar, demoOnly: true }, // Content repurposing agent
  // { agent_id: "18", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar, demoOnly: true }, // LinkedIn campaign analyzer demo agent
];

// Add production-specific mappings here
const PROD_AGENT_CATEGORY_MAP: DemoAgentCategoryMap = [
  // agents which are self served
  { agent_id: "13", category: "Marketing", avatar: GoogleAdsCampaignAnalyserAvatar }, // Google ads agent
  { agent_id: "2", category: "Marketing", avatar: Avatar1 }, // Landing Page Comparator
  { agent_id: "7", category: "Marketing", avatar: Avatar2 }, // Influencer Styled LinkedIn Post Generator
  { agent_id: "8", category: "Marketing", avatar: BlogPostContentStrategizerAvatar }, // Blog Post Content Strategizer
  { agent_id: "14", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar }, // LinkedIn campaign analyzer
  { agent_id: "17", category: "Marketing", avatar: Avatar6 }, // Blog Post Refresh - RZP
  { agent_id: "20", category: "Marketing", avatar: NegativeKeywordAnalyzerAvatar }, // Negative Keyword Analyzer
  { agent_id: "23", category: "Marketing", avatar: Avatar18 }, // AEO
  { agent_id: "28", category: "Marketing", avatar: SeoAuditAvatar }, // SEO Audit

  // demo only agents
  { agent_id: "3", category: "Marketing", avatar: Avatar4, demoOnly: true }, // lead funnel analyser
  { agent_id: "4", category: "Marketing", avatar: Avatar15, demoOnly: true }, // Google Ads Campaign Analyser demo
  { agent_id: "5", category: "Sales", avatar: Avatar6, demoOnly: true }, // Lead Enrichment
  { agent_id: "9", category: "Sales", avatar: Avatar5, demoOnly: true }, // Outbound Call AI Agent
  { agent_id: "10", category: "Marketing", avatar: Avatar2, demoOnly: true }, // Keyword Optimizer
  { agent_id: "11", category: "Finance", avatar: Avatar6, demoOnly: true }, // recki
  { agent_id: "6", category: "Sales", avatar: Avatar1, demoOnly: true }, // sales BANT qualifier,
  { agent_id: "12", category: "Sales", avatar: ProspectCallOpportunityAnalyzerAvatar, demoOnly: true }, // Prospect Call Opportunity Analyzer,
  { agent_id: "15", category: "Marketing", avatar: BlogPostRefreshAvatar, demoOnly: true }, // Blog Post Refresh - hevo
  // { agent_id: "16", category: "Marketing", avatar: LinkedInCampaignAnalyserAvatar, demoOnly: true }, // LinkedIn campaign analyzer demo agent
  { agent_id: "16", category: "Marketing", avatar: ContentRepurposeAvatar, demoOnly: true }, // Content repurposing agent
];

export function getDemoAgentCategoryMap(): DemoAgentCategoryMap {
  const env = import.meta.env.MODE || import.meta.env.VITE_APP_NODE_ENV || "development";
  if (env === "production") return PROD_AGENT_CATEGORY_MAP;
  if (env === "staging") return STAGING_AGENT_CATEGORY_MAP;
  return DEV_AGENT_CATEGORY_MAP;
}

// Helper function to get category for a specific agent ID
export function getCategoryForAgentId(agentId: string): AgentCategory | undefined {
  const categoryMap = getDemoAgentCategoryMap();
  return categoryMap.find(mapping => mapping.agent_id === agentId)?.category;
}

// Helper function to get avatar for a specific agent ID
export function getAvatarForAgentId(agentId: string): string | undefined {
  const categoryMap = getDemoAgentCategoryMap();
  return categoryMap.find(mapping => mapping.agent_id === agentId)?.avatar;
}

// Helper function to get full mapping for a specific agent ID
export function getAgentMapping(agentId: string): AgentCategoryMapping | undefined {
  const categoryMap = getDemoAgentCategoryMap();
  return categoryMap.find(mapping => mapping.agent_id === agentId);
}

export function isDemoOnlyAgent(agentId: string): boolean {
  const categoryMap = getDemoAgentCategoryMap();
  const mapping = categoryMap.find(mapping => mapping.agent_id === agentId);
  
  // If agent is not in the mapping, it's demo only by default
  if (!mapping) return true;
  
  // If agent is in mapping, return the demoOnly value (defaults to false if not specified)
  return mapping.demoOnly === true;
} 
